import React, { useState } from 'react';
import './ActivityLogCard.css';

const ActivityLogCard = ({ log, isFirst, isLast }) => {
  const [expanded, setExpanded] = useState(false);

  // Icônes par type d'action
  const getActionIcon = (actionType) => {
    const icons = {
      'create': '✨',
      'update': '✏️',
      'delete': '🗑️',
      'send': '📤',
      'relaunch': '🔁',
      'comment': '💬',
      'sign': '✅',
      'assign': '👤',
      'status_change': '🔄',
      'archive': '📁',
      'restore': '🔄',
      // Phase 2 - Nouvelles actions
      'tag': '🏷️',
      'unassign': '👤❌',
      'notification': '🔔'
    };
    return icons[actionType] || '📋';
  };

  // Couleurs par type d'action
  const getActionColor = (actionType) => {
    const colors = {
      'create': '#00C851',
      'update': '#FF8800',
      'delete': '#FF4444',
      'send': '#0E4D64',
      'relaunch': '#00B5AD',
      'comment': '#6C757D',
      'sign': '#00C851',
      'assign': '#6F42C1',
      'status_change': '#17A2B8',
      'archive': '#6C757D',
      'restore': '#28A745',
      // Phase 2 - Nouvelles couleurs
      'tag': '#FF6B6B',
      'unassign': '#ADB5BD',
      'notification': '#20C997'
    };
    return colors[actionType] || '#6C757D';
  };

  // Libellés des actions en français
  const getActionLabel = (actionType) => {
    const labels = {
      'create': 'Création',
      'update': 'Modification',
      'delete': 'Suppression',
      'send': 'Envoi',
      'relaunch': 'Relance',
      'comment': 'Commentaire',
      'sign': 'Signature',
      'assign': 'Assignation',
      'status_change': 'Changement de statut',
      'archive': 'Archivage',
      'restore': 'Restauration',
      // Phase 2 - Nouveaux libellés
      'tag': 'Mention utilisateur',
      'unassign': 'Désassignation',
      'notification': 'Notification'
    };
    return labels[actionType] || actionType;
  };

  // Libellés des types de documents
  const getDocumentTypeLabel = (documentType) => {
    const labels = {
      'devis': 'Devis',
      'facture': 'Facture',
      'bon_commande': 'Bon de commande',
      'opportunite': 'Opportunité',
      'operation': 'Opération',
      'societe': 'Société',
      'contact': 'Contact',
      'user': 'Utilisateur'
    };
    return labels[documentType] || documentType;
  };

  // Formatage de la date
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffHours / 24);

    if (diffHours < 1) {
      const diffMinutes = Math.floor(diffMs / (1000 * 60));
      return `Il y a ${diffMinutes} minute${diffMinutes > 1 ? 's' : ''}`;
    } else if (diffHours < 24) {
      return `Il y a ${diffHours} heure${diffHours > 1 ? 's' : ''}`;
    } else if (diffDays < 7) {
      return `Il y a ${diffDays} jour${diffDays > 1 ? 's' : ''}`;
    } else {
      return date.toLocaleDateString('fr-FR', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    }
  };

  // Rendu des détails du metadata
  const renderMetadata = () => {
    if (!log.metadata || Object.keys(log.metadata).length === 0) {
      return null;
    }

    // Rendu spécial pour les actions de tag
    if (log.action_type === 'tag' && log.metadata.tagged_users) {
      return (
        <div className="log-metadata">
          <h4>Utilisateurs mentionnés :</h4>
          <div className="tagged-users-list">
            <span className="tagged-count">
              {log.metadata.tagged_users.length} utilisateur{log.metadata.tagged_users.length > 1 ? 's' : ''} mentionné{log.metadata.tagged_users.length > 1 ? 's' : ''}
            </span>
            {log.metadata.comment_text && (
              <div className="comment-preview">
                <strong>Commentaire :</strong> {log.metadata.comment_text}
              </div>
            )}
          </div>
        </div>
      );
    }

    // Rendu spécial pour les commentaires
    if (log.action_type === 'comment' && log.metadata.tagged_users_count > 0) {
      return (
        <div className="log-metadata">
          <h4>Détails du commentaire :</h4>
          <div className="comment-details">
            {log.metadata.is_reply && (
              <span className="reply-indicator">💬 Réponse</span>
            )}
            <span className="tagged-info">
              👥 {log.metadata.tagged_users_count} utilisateur{log.metadata.tagged_users_count > 1 ? 's' : ''} mentionné{log.metadata.tagged_users_count > 1 ? 's' : ''}
            </span>
          </div>
        </div>
      );
    }

    return (
      <div className="log-metadata">
        <h4>Détails :</h4>
        <div className="metadata-grid">
          {Object.entries(log.metadata).map(([key, value]) => (
            <div key={key} className="metadata-item">
              <span className="metadata-key">{key}:</span>
              <span className="metadata-value">
                {typeof value === 'object' ? JSON.stringify(value, null, 2) : value}
              </span>
            </div>
          ))}
        </div>
      </div>
    );
  };

  // Rendu du contexte (avant/après pour les modifications)
  const renderContext = () => {
    if (!log.context || Object.keys(log.context).length === 0) {
      return null;
    }

    return (
      <div className="log-context">
        <h4>Contexte :</h4>
        <div className="context-grid">
          {Object.entries(log.context).map(([key, value]) => (
            <div key={key} className="context-item">
              <span className="context-key">{key}:</span>
              <span className="context-value">
                {typeof value === 'object' ? JSON.stringify(value, null, 2) : value}
              </span>
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className={`activity-log-card ${isFirst ? 'first' : ''} ${isLast ? 'last' : ''}`}>
      <div className="timeline-marker" style={{ backgroundColor: getActionColor(log.action_type) }}>
        <span className="action-icon">{getActionIcon(log.action_type)}</span>
      </div>

      <div className="log-content">
        <div className="log-header">
          <div className="log-main-info">
            <span className="log-action" style={{ color: getActionColor(log.action_type) }}>
              {getActionLabel(log.action_type)}
            </span>
            <span className="log-separator">•</span>
            <span className="log-document-type">
              {getDocumentTypeLabel(log.document_type)}
            </span>
            {log.document_reference && (
              <>
                <span className="log-separator">•</span>
                <span className="log-document-ref">
                  {log.document_reference}
                </span>
              </>
            )}
          </div>
          <div className="log-time">
            {formatDate(log.timestamp)}
          </div>
        </div>

        <div className="log-user-info">
          <span className="log-user">
            👤 {log.user_name}
          </span>
          {log.user_email && (
            <span className="log-email">
              ({log.user_email})
            </span>
          )}
        </div>

        {/* Aperçu des métadonnées importantes */}
        {log.metadata && Object.keys(log.metadata).length > 0 && (
          <div className="log-preview">
            {log.metadata.comment_preview && (
              <div className="preview-comment">
                💬 {log.metadata.comment_preview}
              </div>
            )}
            {log.metadata.recipient_email && (
              <div className="preview-recipient">
                📧 Envoyé à: {log.metadata.recipient_email}
              </div>
            )}
            {log.metadata.status_from && log.metadata.status_to && (
              <div className="preview-status">
                🔄 {log.metadata.status_from} → {log.metadata.status_to}
              </div>
            )}
            {log.metadata.fields_changed && (
              <div className="preview-fields">
                ✏️ Champs modifiés: {Array.isArray(log.metadata.fields_changed) 
                  ? log.metadata.fields_changed.join(', ') 
                  : log.metadata.fields_changed}
              </div>
            )}
          </div>
        )}

        {/* Bouton expand/collapse */}
        {((log.metadata && Object.keys(log.metadata).length > 0) || 
          (log.context && Object.keys(log.context).length > 0)) && (
          <div className="log-actions">
            <button
              className="expand-button"
              onClick={() => setExpanded(!expanded)}
            >
              {expanded ? '🔽 Masquer les détails' : '🔼 Voir les détails'}
            </button>
          </div>
        )}

        {/* Détails expandables */}
        {expanded && (
          <div className="log-details">
            {renderMetadata()}
            {renderContext()}
            
            {/* Informations techniques */}
            <div className="log-technical">
              <h4>Informations techniques :</h4>
              <div className="technical-info">
                <div className="technical-item">
                  <span className="technical-label">ID:</span>
                  <span className="technical-value">{log.id}</span>
                </div>
                <div className="technical-item">
                  <span className="technical-label">Date exacte:</span>
                  <span className="technical-value">
                    {new Date(log.timestamp).toLocaleString('fr-FR')}
                  </span>
                </div>
                {log.ip_address && (
                  <div className="technical-item">
                    <span className="technical-label">IP:</span>
                    <span className="technical-value">{log.ip_address}</span>
                  </div>
                )}
                {log.user_agent && (
                  <div className="technical-item">
                    <span className="technical-label">User Agent:</span>
                    <span className="technical-value">{log.user_agent}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ActivityLogCard;